'use client';

import { type FC, useContext, useState } from 'react';
import { Button, ButtonGroup, Flex } from '@/_components';
import styles from './EnterpriseDialog.module.css';
import { EnterpriseForm } from './_components/EnterpriseForm';
import { SentEnterpriseForm } from './_components/SentEnterpriseForm';
import type {
  FormStateType,
  EnterpriseDialogProps,
} from './EnterpriseDialog.types';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { sendHubSpotEvent } from '@/_api/hubspot-events';

const HUBSPOT_EVENT_NAMES: Record<
  FormStateType,
  'enterprise_contact_sales' | 'enterprise_book_demo'
> = {
  'contact-sales': 'enterprise_contact_sales',
  'book-a-demo': 'enterprise_book_demo',
};

export const EnterpriseDialog: FC<EnterpriseDialogProps> = ({
  children,
  isDisabled,
  description,
  onClose,
  items,
  title,
}) => {
  const { addToast } = useContext(ToastContext);
  const { firm } = useContext(FirmSettingsContext);
  const { user, token } = useContext(UserContext);

  const [formState, setFormState] = useState<FormStateType | undefined>(
    undefined,
  );

  const onSetForm = async (formState?: FormStateType) => {
    try {
      if (formState && user?.email && token) {
        const eventName = HUBSPOT_EVENT_NAMES[formState];
        const eventProperties = {
          user_id: user.id,
          firm_id: firm?.id,
          firm_name: firm?.name,
        };

        console.debug('Sending HubSpot event:', eventName, eventProperties);

        sendHubSpotEvent({
          token,
          eventName,
          properties: eventProperties,
        }).catch((error) => {
          console.error('Failed to send HubSpot event:', error);
        });
      }

      setFormState(formState);
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  if (isDisabled) return children;

  return (
    <Flex className={styles.enterpriseDialogContainer}>
      <Flex className={styles.enterpriseDialog} isColumn={true}>
        {onClose && (
          <ButtonGroup>
            <Button
              intent="secondary"
              onClick={onClose}
              iconLeft="x"
              size="small"
              value="Close"
              isIconOnly={true}
              className={styles.enterpriseDialog__close}
            />
          </ButtonGroup>
        )}
        {!formState ? (
          <EnterpriseForm
            description={description}
            title={title}
            items={items}
            onClick={onSetForm}
          />
        ) : (
          <SentEnterpriseForm formState={formState} />
        )}
      </Flex>
      <div className={styles.enterpriseDialog__content}>{children}</div>
    </Flex>
  );
};
